import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar } from "lucide-react";

function isHtml(str) {
  return str && (str.trim().startsWith('<') || /<[a-z][\s\S]*>/i.test(str));
}

export default function NewsDetailPage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/content/news/${id}`).then(res => {
      setArticle(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  usePageTitle(article?.title || "News");

  if (loading) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-24">
        <Skeleton className="h-10 w-2/3 mb-6" />
        <Skeleton className="h-64 w-full mb-6 rounded-2xl" />
        <Skeleton className="h-4 w-full mb-3" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-jci-black mb-2">Article Not Found</h2>
          <Link to="/news-press"><Button variant="outline">Back to News</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="news-detail-page">
      <div className="bg-jci-black py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <Link to="/news-press" className="inline-flex items-center gap-2 text-white/50 hover:text-white mb-4 text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to News
          </Link>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight" data-testid="news-detail-title">{article.title}</h1>
          <p className="text-white/50 mt-3 text-sm flex items-center gap-2">
            <Calendar className="w-4 h-4" /> {article.published_date}
          </p>
        </div>
        <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy mt-8" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl py-16">
        {article.image_url && (
          <img src={article.image_url} alt={article.title} className="w-full h-64 md:h-96 object-cover rounded-2xl mb-10 shadow-lg" />
        )}
        <div data-testid="news-detail-content">
          {isHtml(article.content) ? (
            <div className="prose prose-lg max-w-none prose-headings:text-jci-black prose-p:text-slate-600 prose-a:text-jci-blue" dangerouslySetInnerHTML={{ __html: article.content }} />
          ) : (
            <div className="prose prose-lg max-w-none">
              {article.content?.split('\n').map((p, i) => (
                p.trim() ? <p key={i} className="text-slate-600 leading-relaxed mb-4">{p}</p> : <br key={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
